import * as _ from 'lodash';
import {
  ColumnsType,
  FilterValuesType,
  NumericOperator,
  Selection,
  SpecialOperator,
  StringOperator,
} from '../redux/actions';
import * as bodybuilder from 'bodybuilder';
import {ESQueryParams} from 'cl-api';
import {SortProps, SortPropsForQuery} from '../consts';

export type ESQuery = ESQueryParams['query'];

function tableFilterToBodybuilder(filter: FilterValuesType, query?: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder {
  if (!query) {
    query = bodybuilder();
  }
  _.forEach(filter, (filterValue, columnName) => {
    const arrayFilterValue = getArrayIfArrayString(filterValue);
    if (arrayFilterValue) {
      query = query.filter('terms', columnName, arrayFilterValue);
    } else if (_.isString(filterValue) || _.isNumber(filterValue) || _.isArray(filterValue)) {
      query = query.filter('match', columnName, filterValue);
    } else {
      const {value, operator} = filterValue;
      switch (operator) {
        case NumericOperator.GTE:
          query = query.filter('range', columnName, {gte: value});
          break;
        case NumericOperator.GT:
          query = query.filter('range', columnName, {gt: value});
          break;
        case NumericOperator.LT:
          query = query.filter('range', columnName, {lt: value});
          break;
        case NumericOperator.LTE:
          query = query.filter('range', columnName, {lte: value});
          break;
        case NumericOperator.EQL:
          query = query.filter('match', columnName, value);
          break;
        case NumericOperator.NEQ:
          query = query.notFilter('match', columnName, value);
          break;
        case StringOperator.CONTAINS:
          query = query.filter('wildcard', columnName, `*${_.toLower(value as string)}*`);
          break;
        case StringOperator.NOT_CONTAINS:
          query = query.notFilter('match', columnName, value);
          break;
        case StringOperator.EQUALS:
          query = query.filter('match_phrase', columnName, value);
          break;
        case StringOperator.NOT_EQUALS:
          query = query.notFilter('match_phrase', columnName, value);
          break;
        case StringOperator.STARTS_WITH:
          query = query.filter('prefix', `${columnName}.keyword`, value);
          break;
        case SpecialOperator.TERMS_LOOKUP:
          query = query.filter('terms', '_id', {type: '_doc', ..._.omit(filterValue, ['operator', 'value'])});
          break;
      }
    }
  });
  return query;
}

function selectedIdsFilter(include: Selection['include'], query?: bodybuilder.Bodybuilder) {
  if (!query) {
    query = bodybuilder();
  }
  return query.filter('ids', {values: include});
}

export function tableFilterToEsQuery(filter: FilterValuesType): ESQuery {
  if (_.isEmpty(filter)) {
    return null;
  }
  let query = tableFilterToBodybuilder(filter);
  return _.get(query.build(), 'query.bool.filter');
}

export function tableSelectionToEsQuery(filter: FilterValuesType | null, selection: Selection): ESQuery {
  let query: bodybuilder.Bodybuilder;
  query = tableFilterToBodybuilder(filter);
  if (selection.exclude) {
    query = query.notFilter('ids', {values: selection.exclude});
  } else {
    query = selectedIdsFilter(selection.include, query);
  }
  return _.get(query.build(), 'query.bool.filter');
}

export function singleItemQuery(id) {
  return tableSelectionToEsQuery(null, {include: [id]});
}

export function getColumnsSortProps(sortProps: SortProps, columns: ColumnsType): SortPropsForQuery {
  if (sortProps) {
    const fixedValuesSortProps = _.mapValues(sortProps, (value, columnName) => {
      if (columns[columnName].iconOnly) {
        return {order: value, missing: value === 'asc' ? '_first' : '_last'};
      }
      return value;
    });
    return _.mapKeys(fixedValuesSortProps, (value, columnName) => {
      if (_.get(columns[columnName].fields, 'keyword')) {
        return `${columnName}.keyword`;
      }
      return columnName;
    });
  }
  return sortProps;
}

const getArrayIfArrayString = value => {
  if (_.isString(value)) {
    try {
      const result = JSON.parse(value);
      if (_.isArray(result)) {
        return result;
      }
    } catch (e) {
      return false;
    }
  }
  return false;
};
